import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type WheelEvent,
} from 'react';
import clsx from 'clsx';
import type { CameraTimeline, TimelineSegment } from '@api/types';
import { formatLocalYmd } from '@lib/date-ymd';

const STORAGE_COLORS = {
  local: 'bg-accent',
  // nas: 'bg-success',
} as const;

const EMPTY_SEGMENTS: TimelineSegment[] = [];

const HOUR_LABELS = ['00:00', '06:00', '12:00', '18:00', '24:00'] as const;

interface Props {
  timeline: CameraTimeline | null;
  selectedSegmentId?: string;
  isLoading: boolean;
  /** When set, blocks the timeline strip and shows this label with a spinner (fetch, playback prep, or buffering). */
  busyMessage?: string | null;
  error: string | null;
  onSelectSegment: (segment: TimelineSegment) => void;
  onRefresh: () => void;
  isLive: boolean;
  playbackWallClockMs: number | null;
  selectedDate: string;
  onDateChange: (date: string) => void;
  showJumpToToday: boolean;
  onJumpToToday: () => void;
  isPlaying: boolean;
  playPauseDisabled: boolean;
  onPlayPause: () => void;
  onPreviousCamera: () => void;
  onNextCamera: () => void;
  canPreviousCamera: boolean;
  canNextCamera: boolean;
  seekStepSeconds: number;
  /** When true, rewind / fast-forward are disabled (e.g. live stream). */
  seekDisabled: boolean;
  onRewind: () => void;
  onFastForward: () => void;
  canGoLive: boolean;
  onGoLive: () => void;
  timelineInteractive: boolean;
  onTimelineSeek: (wallClockMs: number) => void;
  /** When true, Shift+drag sets export range and export helper copy is shown. */
  exportSelectionActive?: boolean;
  /** Leave export picking mode and clear the range. */
  onCancelExportSelection?: () => void;
  /** Current clip-export range on the day axis (wall ms). Null if unset. */
  exportRangeWallMs: { start: number; end: number } | null;
  onExportRangeWallMsChange: (range: { start: number; end: number } | null) => void;
}

export function PlaybackTimeline({
  timeline,
  selectedSegmentId,
  isLoading,
  busyMessage = null,
  error,
  onSelectSegment,
  onRefresh,
  isLive,
  playbackWallClockMs,
  selectedDate,
  onDateChange,
  showJumpToToday,
  onJumpToToday,
  isPlaying,
  playPauseDisabled,
  onPlayPause,
  onPreviousCamera,
  onNextCamera,
  canPreviousCamera,
  canNextCamera,
  seekStepSeconds,
  seekDisabled,
  onRewind,
  onFastForward,
  canGoLive,
  onGoLive,
  timelineInteractive,
  onTimelineSeek,
  exportSelectionActive = false,
  onCancelExportSelection,
  exportRangeWallMs,
  onExportRangeWallMsChange,
}: Props): JSX.Element {
  const [zoom, setZoom] = useState(4);
  const [exportDragPreview, setExportDragPreview] = useState<{ start: number; end: number } | null>(
    null,
  );
  const dateInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackInnerRef = useRef<HTMLDivElement>(null);
  const prevZoomForScrollRef = useRef(zoom);
  const playheadPercentRef = useRef<number | null>(null);

  const [liveNowMs, setLiveNowMs] = useState(() => Date.now());
  const viewingLocalToday = selectedDate === formatLocalYmd(new Date());

  useEffect(() => {
    if (!isLive || !viewingLocalToday) return undefined;
    setLiveNowMs(Date.now());
    const id = window.setInterval(() => setLiveNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isLive, viewingLocalToday]);

  const segments = timeline?.segments ?? EMPTY_SEGMENTS;
  const bounds = useMemo(() => {
    const dayStart = timeline ? new Date(timeline.dayStart).getTime() : 0;
    const dayEnd = timeline ? new Date(timeline.dayEnd).getTime() : dayStart + 1;
    return { dayStart, duration: Math.max(1, dayEnd - dayStart) };
  }, [timeline]);

  const activePlayheadWallMs = useMemo(() => {
    if (!timeline) return null;
    const dayEnd = bounds.dayStart + bounds.duration;
    if (isLive && viewingLocalToday) {
      const now = liveNowMs;
      return Math.min(dayEnd - 1, Math.max(bounds.dayStart, now));
    }
    if (!isLive && playbackWallClockMs !== null) {
      if (playbackWallClockMs < bounds.dayStart || playbackWallClockMs > dayEnd) return null;
      return playbackWallClockMs;
    }
    return null;
  }, [
    timeline,
    isLive,
    viewingLocalToday,
    liveNowMs,
    playbackWallClockMs,
    bounds.dayStart,
    bounds.duration,
  ]);

  const playheadPercent = useMemo(() => {
    if (activePlayheadWallMs === null || !timeline) return null;
    return ((activePlayheadWallMs - bounds.dayStart) / bounds.duration) * 100;
  }, [activePlayheadWallMs, timeline, bounds.dayStart, bounds.duration]);

  playheadPercentRef.current = playheadPercent;

  /** Keep the playhead in view when switching day or zoom (not on every live clock tick). */
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const inner = trackInnerRef.current;
    const pct = playheadPercentRef.current;
    if (!scroller || !inner || pct === null) return;
    const viewW = scroller.clientWidth;
    const contentW = inner.offsetWidth;
    if (contentW <= viewW) return;
    const px = (pct / 100) * contentW;
    const maxScroll = Math.max(0, contentW - viewW);
    scroller.scrollLeft = Math.max(0, Math.min(maxScroll, px - viewW / 2));
  }, [selectedDate, zoom, bounds.dayStart]);

  const totalDurationSeconds = useMemo(
    () => segments.reduce((total, segment) => total + segment.durationSeconds, 0),
    [segments],
  );

  const segmentItems = useMemo(() => {
    const dayEnd = bounds.dayStart + bounds.duration;
    return segments
      .map((segment) => {
        const segStartMs = new Date(segment.startTime).getTime();
        const segEndMs = new Date(segment.endTime).getTime();
        const clipStart = Math.max(segStartMs, bounds.dayStart);
        const clipEnd = Math.min(segEndMs, dayEnd);
        if (clipEnd <= clipStart) return null;
        const left = ((clipStart - bounds.dayStart) / bounds.duration) * 100;
        const width = ((clipEnd - clipStart) / bounds.duration) * 100;
        return {
          segment,
          left: `${Math.max(0, left)}%`,
          width: `${Math.max(0.08, width)}%`,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [bounds, segments]);

  /** Keep the viewport center fixed on the day axis when zoom level changes (wheel or +/-). */
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    const prevZ = prevZoomForScrollRef.current;
    if (prevZ === zoom) return;

    if (scroller) {
      const viewW = scroller.clientWidth;
      if (viewW > 0) {
        const oldContentW = prevZ * viewW;
        const newContentW = zoom * viewW;
        if (oldContentW > 0 && newContentW > 0) {
          const centerPx = scroller.scrollLeft + viewW / 2;
          const anchorFrac = centerPx / oldContentW;
          const maxScroll = Math.max(0, newContentW - viewW);
          const nextScroll = anchorFrac * newContentW - viewW / 2;
          scroller.scrollLeft = Math.max(0, Math.min(maxScroll, nextScroll));
        }
      }
    }

    prevZoomForScrollRef.current = zoom;
  }, [zoom]);

  const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    // Horizontal pan (trackpad / shift+wheel): let the browser scroll — do not zoom.
    const absX = Math.abs(event.deltaX);
    const absY = Math.abs(event.deltaY);
    const verticalDominant = absY > absX * 1.08;
    const pinchLike =
      (event.ctrlKey || event.metaKey) && absY > 0 && absX <= absY * 1.5;
    if (!verticalDominant && !pinchLike) {
      return;
    }

    event.preventDefault();
    setZoom((current) => {
      const next = event.deltaY < 0 ? current + 0.5 : current - 0.5;
      return Math.min(8, Math.max(1, next));
    });
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((z) => Math.max(1, z - 0.5));
  }, []);

  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(8, z + 0.5));
  }, []);

  const visibleSpanLabel = useMemo(() => formatVisibleSpanLabel(zoom), [zoom]);

  const playheadTimeLabel = useMemo(() => {
    if (activePlayheadWallMs === null) return '';
    return formatWallClockTime(activePlayheadWallMs);
  }, [activePlayheadWallMs]);

  const exportDragTimeLabel = useMemo(() => {
    if (!exportDragPreview) return '';
    return `${formatWallClockTime(exportDragPreview.start)} – ${formatWallClockTime(exportDragPreview.end)}`;
  }, [exportDragPreview]);

  const formattedSelectedDate = useMemo(
    () => formatCalendarHeading(selectedDate),
    [selectedDate],
  );

  const wallMsFromClientX = useCallback(
    (clientX: number): number => {
      const inner = trackInnerRef.current;
      const scroller = scrollRef.current;
      if (!inner || !scroller || !timeline) return bounds.dayStart;
      const viewRect = scroller.getBoundingClientRect();
      const xInContent = clientX - viewRect.left + scroller.scrollLeft;
      const total = inner.offsetWidth;
      if (total <= 0) return bounds.dayStart;
      const frac = Math.min(1, Math.max(0, xInContent / total));
      const raw = bounds.dayStart + frac * bounds.duration;
      return Math.min(bounds.dayStart + bounds.duration - 1, Math.max(bounds.dayStart, raw));
    },
    [timeline, bounds.dayStart, bounds.duration],
  );

  const handleExportPointerDownCapture = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (
        !exportSelectionActive ||
        !event.shiftKey ||
        !timelineInteractive ||
        !timeline ||
        event.button !== 0
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const anchor = wallMsFromClientX(event.clientX);
      const pointerId = event.pointerId;
      setExportDragPreview({ start: anchor, end: anchor });

      const clampToDay = (a: number, b: number): { start: number; end: number } => {
        const dayEnd = bounds.dayStart + bounds.duration;
        const start = Math.max(bounds.dayStart, Math.min(Math.min(a, b), dayEnd - 1));
        const end = Math.max(bounds.dayStart + 1, Math.min(Math.max(a, b), dayEnd));
        return start < end ? { start, end } : { start: bounds.dayStart, end: bounds.dayStart + 1 };
      };

      const onMove = (ev: PointerEvent): void => {
        if (ev.pointerId !== pointerId) return;
        const w = wallMsFromClientX(ev.clientX);
        setExportDragPreview(clampToDay(anchor, w));
      };

      const onUp = (ev: PointerEvent): void => {
        if (ev.pointerId !== pointerId) return;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        const w = wallMsFromClientX(ev.clientX);
        const { start, end } = clampToDay(anchor, w);
        setExportDragPreview(null);
        const MIN_EXPORT_MS = 2000;
        if (end - start >= MIN_EXPORT_MS) {
          onExportRangeWallMsChange({ start, end });
        }
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [
      timelineInteractive,
      exportSelectionActive,
      timeline,
      wallMsFromClientX,
      bounds.dayStart,
      bounds.duration,
      onExportRangeWallMsChange,
    ],
  );

  const handleTrackPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!timelineInteractive || !timeline || isLive || event.button !== 0) return;
      if (event.shiftKey) return;
      if ((event.target as Element).closest('button[type="button"]')) return;
      const pointerId = event.pointerId;
      const startX = event.clientX;
      const up = (ev: PointerEvent): void => {
        if (ev.pointerId !== pointerId) return;
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        if (Math.abs(ev.clientX - startX) < 10) {
          onTimelineSeek(wallMsFromClientX(ev.clientX));
        }
      };
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    },
    [timelineInteractive, timeline, isLive, onTimelineSeek, wallMsFromClientX],
  );

  const rewindTitle = `Back ${seekStepSeconds} s`;
  const forwardTitle = `Forward ${seekStepSeconds} s`;

  return (
    <section className="shrink-0 rounded-lg border border-border bg-surface p-2.5 shadow-card">
      <div className="mb-2 flex flex-wrap items-center gap-2 border-b border-border pb-2">
        <div className="flex min-w-[10rem] flex-1 flex-wrap items-center gap-1.5">
          <input
            ref={dateInputRef}
            type="date"
            value={selectedDate}
            max={formatLocalYmd(new Date())}
            onChange={(e) => onDateChange(e.target.value)}
            className="sr-only"
            aria-label="Select playback date"
          />
          <button
            type="button"
            onClick={() => dateInputRef.current?.showPicker?.() ?? dateInputRef.current?.click()}
            className={clsx(
              'inline-flex items-center gap-1.5 rounded-md border border-border bg-bg px-2.5 py-1.5 text-xs font-medium text-foreground',
              'transition-colors hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            )}
          >
            <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-muted" />
            <span>{formattedSelectedDate}</span>
            <ChevronDownIcon className="h-3.5 w-3.5 shrink-0 text-muted" />
          </button>
          {showJumpToToday ? (
            <button
              type="button"
              onClick={onJumpToToday}
              className="rounded-md border border-border px-2 py-1.5 text-[11px] font-medium text-muted hover:border-accent hover:text-foreground"
            >
              Today
            </button>
          ) : null}
        </div>

        <div className="flex flex-1 flex-wrap items-center justify-center gap-0.5 sm:gap-1">
          <TransportIconButton
            label="Previous camera"
            title={canPreviousCamera ? 'Previous camera' : 'No previous camera'}
            disabled={!canPreviousCamera}
            onClick={onPreviousCamera}
          >
            <SkipBackIcon />
          </TransportIconButton>
          <TransportIconButton
            label={`Rewind ${seekStepSeconds} seconds`}
            title={seekDisabled ? 'Only available when viewing a recording' : rewindTitle}
            disabled={seekDisabled}
            onClick={onRewind}
          >
            <RewindIcon />
          </TransportIconButton>
          <button
            type="button"
            onClick={onPlayPause}
            disabled={playPauseDisabled}
            title={
              playPauseDisabled
                ? 'Play and pause are only available when viewing a recording'
                : isPlaying
                  ? 'Pause'
                  : 'Play'
            }
            className={clsx(
              'flex h-9 w-9 items-center justify-center rounded-full border-2 border-foreground/30 text-foreground',
              'transition-colors hover:border-accent hover:text-accent',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              playPauseDisabled && 'cursor-not-allowed opacity-40 hover:border-foreground/30 hover:text-foreground',
            )}
          >
            {isPlaying ? <PauseGlyph /> : <PlayGlyph />}
          </button>
          <TransportIconButton
            label={`Fast forward ${seekStepSeconds} seconds`}
            title={seekDisabled ? 'Only available when viewing a recording' : forwardTitle}
            disabled={seekDisabled}
            onClick={onFastForward}
          >
            <FastForwardIcon />
          </TransportIconButton>
          <TransportIconButton
            label="Next camera"
            title={canNextCamera ? 'Next camera' : 'No next camera'}
            disabled={!canNextCamera}
            onClick={onNextCamera}
          >
            <SkipForwardIcon />
          </TransportIconButton>
        </div>

        <div className="flex min-w-[6rem] flex-1 justify-end">
          {canGoLive ? (
            <button
              type="button"
              onClick={onGoLive}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white',
                'shadow-sm transition-colors hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80',
              )}
            >
              <BroadcastIcon className="h-3.5 w-3.5" />
              Go live
            </button>
          ) : (
            <span className="text-[11px] text-muted">Live</span>
          )}
        </div>
      </div>

      <p className="mb-1.5 text-[10px] text-muted">
        {segments.length} segments · {formatDuration(totalDurationSeconds)} recorded on this day.
      </p>

      {error ? (
        <div className="mb-1.5 flex items-center justify-between gap-2 rounded-md border border-danger/40 bg-danger/10 px-2 py-1.5 text-[11px]">
          <span className="text-danger">{error}</span>
          <button type="button" onClick={onRefresh} className="text-foreground underline">
            Retry
          </button>
        </div>
      ) : null}

      <div
        ref={scrollRef}
        className="scrollbar-hide overflow-x-auto rounded-md border border-border bg-bg p-1.5"
        onWheel={handleWheel}
      >
        <div ref={trackInnerRef} className="relative min-w-full" style={{ width: `${zoom * 100}%` }}>
          <div className="relative w-full pt-5">
            <div
              className={clsx(
                'relative h-8 w-full',
                timelineInteractive && 'cursor-pointer',
                !timelineInteractive && 'cursor-default opacity-90',
              )}
              onPointerDownCapture={handleExportPointerDownCapture}
              onPointerDown={handleTrackPointerDown}
            >
              <div className="pointer-events-none absolute inset-x-0 top-1/2 h-2 -translate-y-1/2 rounded-sm bg-border/60" />

              {exportSelectionActive && exportRangeWallMs && !exportDragPreview ? (
                <div
                  className="pointer-events-none absolute inset-y-0 z-[2] rounded-sm border border-accent/70 bg-accent/20"
                  style={rangeToStyle(exportRangeWallMs, bounds.dayStart, bounds.duration)}
                  title="Clip export range"
                />
              ) : null}

              {exportDragPreview ? (
                <>
                  <div
                    className="pointer-events-none absolute inset-y-0 z-[3] rounded-sm border border-dashed border-warning bg-warning/15"
                    style={rangeToStyle(exportDragPreview, bounds.dayStart, bounds.duration)}
                    title="Adjusting export range"
                  />
                  <div
                    className="pointer-events-none absolute -top-5 z-[25] max-w-[min(100%,18rem)] -translate-x-1/2 rounded bg-warning px-2 py-0.5 text-center text-[10px] font-semibold tabular-nums text-black shadow-sm"
                    style={{
                      left: `${
                        ((((exportDragPreview.start + exportDragPreview.end) / 2 - bounds.dayStart) /
                          bounds.duration) *
                          100)
                      }%`,
                    }}
                  >
                    {exportDragTimeLabel}
                  </div>
                </>
              ) : null}

              {Array.from({ length: 24 }, (_, hour) => (
                <div
                  key={hour}
                  className="pointer-events-none absolute inset-y-0 w-px bg-border/35"
                  style={{ left: `${(hour / 24) * 100}%` }}
                />
              ))}

              {playheadPercent !== null ? (
                <>
                  {exportDragPreview ? null : (
                    <div
                      className="pointer-events-none absolute -top-5 z-20 -translate-x-1/2 rounded bg-accent px-2 py-0.5 text-[10px] font-semibold tabular-nums text-white"
                      style={{ left: `${playheadPercent}%` }}
                      title={isLive && viewingLocalToday ? 'Current time (live)' : 'Playback position'}
                    >
                      {playheadTimeLabel}
                    </div>
                  )}
                  <div
                    className="pointer-events-none absolute inset-y-0 z-10 w-0.5 -translate-x-1/2 rounded-full bg-accent"
                    style={{ left: `${playheadPercent}%` }}
                    aria-hidden
                  />
                </>
              ) : null}

              {segmentItems.map(({ segment, left, width }) => {
                return (
                  <button
                    key={segment.id}
                    type="button"
                    title={`${formatAxisTime(segment.startTime)} – ${formatAxisTime(segment.endTime)} (${segment.storageTarget.toUpperCase()})`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectSegment(segment);
                    }}
                    className={clsx(
                      'absolute top-1/2 z-[5] h-5 -translate-y-1/2 rounded-sm transition',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                      segment.storageTarget === 'local'
                        ? STORAGE_COLORS.local
                        : STORAGE_COLORS.local /* NAS disabled */,
                      segment.status === 'in-progress' &&
                        selectedSegmentId !== segment.id &&
                        'ring-1 ring-dashed ring-white/60',
                      selectedSegmentId === segment.id
                        ? 'ring-2 ring-white ring-offset-1 ring-offset-bg'
                        : 'hover:brightness-110',
                    )}
                    style={{ left, width }}
                  />
                );
              })}

              {busyMessage ? (
                <div className="pointer-events-none absolute inset-0 z-[15] flex flex-col items-center justify-center gap-2 bg-black/45 text-[10px] font-medium uppercase tracking-wide text-white backdrop-blur-[1px]">
                  <TimelineSpinner className="h-6 w-6 text-accent" />
                  <span className="max-w-[14rem] px-2 text-center normal-case leading-snug tracking-normal">
                    {busyMessage}
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-1 flex h-4 w-full justify-between text-[9px] tabular-nums text-muted">
            {HOUR_LABELS.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[10px]">
        <div className="flex flex-wrap items-center gap-2.5 text-foreground">
          {/* <Legend color={STORAGE_COLORS.nas} label="NAS" /> */}
          <Legend color={STORAGE_COLORS.local} label="HDD" />
          <span className="inline-flex items-center gap-1 text-muted">
            <span className="h-2 w-2 shrink-0 rounded-sm bg-border" />
            Gap
          </span>
        </div>
        <div className="max-w-[20rem] flex-1 text-center text-[10px] leading-snug text-muted sm:max-w-md">
          <p>Click timeline to seek. Scroll horizontally to pan when zoomed.</p>
          {timelineInteractive && exportSelectionActive ? (
            <p className="mt-0.5 text-foreground/90">
              <span className="font-semibold text-accent">Shift+drag</span> on the timeline to set clip export
              start and end (one MP4). Then use Export again in the player toolbar to download.
            </p>
          ) : null}
          {exportSelectionActive && exportRangeWallMs ? (
            <p className="mt-1 flex flex-wrap items-center justify-center gap-2 font-mono text-[10px] text-foreground">
              <span>
                Export: {formatAxisTime(new Date(exportRangeWallMs.start).toISOString())} –{' '}
                {formatAxisTime(new Date(exportRangeWallMs.end).toISOString())}
              </span>
              <button
                type="button"
                onClick={() => onExportRangeWallMsChange(null)}
                className="rounded border border-border px-1.5 py-0.5 font-sans text-[10px] text-muted hover:border-accent hover:text-foreground"
              >
                Clear range
              </button>
              {onCancelExportSelection ? (
                <button
                  type="button"
                  onClick={onCancelExportSelection}
                  className="rounded border border-border px-1.5 py-0.5 font-sans text-[10px] text-muted hover:border-danger hover:text-danger"
                >
                  Cancel export
                </button>
              ) : null}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Zoom out timeline"
            onClick={zoomOut}
            className={clsx(
              'flex h-7 w-7 items-center justify-center rounded-md border border-border bg-bg text-base leading-none text-foreground',
              'transition-colors hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            )}
          >
            −
          </button>
          <span className="min-w-[3rem] text-center font-medium tabular-nums text-muted">
            {visibleSpanLabel}
          </span>
          <button
            type="button"
            aria-label="Zoom in timeline"
            onClick={zoomIn}
            className={clsx(
              'flex h-7 w-7 items-center justify-center rounded-md border border-border bg-bg text-base leading-none text-foreground',
              'transition-colors hover:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            )}
          >
            +
          </button>
        </div>
      </div>

      {!isLoading && !error && segments.length === 0 ? (
        <p className="mt-2 text-[10px] text-muted">
          No recordings for this date. Pick another day above — only past and today are available.
        </p>
      ) : null}
    </section>
  );
}

function TimelineSpinner({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={clsx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-30" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path
        d="M21 12a9 9 0 00-9-9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TransportIconButton({
  label,
  title,
  disabled,
  onClick,
  children,
}: {
  label: string;
  /** Native tooltip; defaults to `label`. */
  title?: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={title ?? label}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'flex h-8 w-8 items-center justify-center rounded-full border border-border bg-bg text-foreground',
        'transition-colors hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        disabled && 'cursor-not-allowed opacity-35 hover:border-border hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function Legend({ color, label }: { color: string; label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 font-medium">
      <span className={clsx('h-2 w-2 shrink-0 rounded-sm', color)} />
      {label}
    </span>
  );
}

function rangeToStyle(
  range: { start: number; end: number },
  dayStart: number,
  duration: number,
): { left: string; width: string } {
  const leftPct = ((range.start - dayStart) / duration) * 100;
  const widthPct = ((range.end - range.start) / duration) * 100;
  return {
    left: `${Math.max(0, leftPct)}%`,
    width: `${Math.max(0.04, widthPct)}%`,
  };
}

function formatCalendarHeading(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

function formatWallClockTime(wallMs: number): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(new Date(wallMs));
}

function formatAxisTime(value: string): string {
  return formatWallClockTime(new Date(value).getTime());
}

function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatVisibleSpanLabel(zoom: number): string {
  const hoursVisible = 24 / zoom;
  if (hoursVisible >= 1) {
    const rounded = hoursVisible >= 10 ? Math.round(hoursVisible) : Number(hoursVisible.toFixed(1));
    return `${rounded} h`;
  }
  const mins = Math.max(1, Math.round(hoursVisible * 60));
  return `${mins} min`;
}

function CalendarIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BroadcastIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 14a2 2 0 100-4 2 2 0 000 4zm-3.5-3.5a5.5 5.5 0 017.78 0l1.06-1.06a7 7 0 00-9.9 0l1.06 1.06zM6.34 8.34a9 9 0 0111.32 0l1.06-1.06A11 11 0 004.28 7.28l1.06 1.06z" />
    </svg>
  );
}

function PlayGlyph(): JSX.Element {
  return (
    <svg className="ml-0.5 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function PauseGlyph(): JSX.Element {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 5h4v14H6V5zm8 0h4v14h-4V5z" />
    </svg>
  );
}

function SkipBackIcon(): JSX.Element {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 6h2v12H6V6zm4 0l10 6-10 6V6z" />
    </svg>
  );
}

function SkipForwardIcon(): JSX.Element {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 6h2v12h-2V6zM6 6l10 6-10 6V6z" />
    </svg>
  );
}

function RewindIcon(): JSX.Element {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11 18V6l-8 6 8 6zm2-12v12l8-6-8-6z" />
    </svg>
  );
}

function FastForwardIcon(): JSX.Element {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M13 6v12l8-6-8-6zM4 6l8 6-8 6V6z" />
    </svg>
  );
}
